export const PACKING_LIST_ADD_ITEM_SPECIAL_PROPERTIES = [
  "hazardous",
  "stackable",
  "crating_required",
  "temperature_control_required",
  "is_dangerous_good",
];

export function resetAddItemField(setValue: (name: string, value: unknown) => void, key: string) {
  if (key === "good_customs_information") setValue(key, [{}]);
  else if (key === "nmfc_class" || key === "un_number") setValue(key, "");
  else if (key === "cbm_scope") setValue(key, "piece");
  else if (PACKING_LIST_ADD_ITEM_SPECIAL_PROPERTIES.includes(key)) setValue(key, false);
  else setValue(key, null);
}

function roundDimensionValue(value: number) {
  return Math.round(value * 100) / 100;
}

function convertDimensionValue(value: number, sourceUnit: string, targetUnit: string) {
  if (!sourceUnit || !targetUnit || sourceUnit === targetUnit) {
    return roundDimensionValue(value);
  }
  if (sourceUnit === "inch" && targetUnit === "cm") {
    return roundDimensionValue(value * 2.54);
  }
  if (sourceUnit === "cm" && targetUnit === "inch") {
    return roundDimensionValue(value / 2.54);
  }
  return roundDimensionValue(value);
}

function shouldShowDimensionFields(handlingUnit: string | null | undefined, handlingUnitSettings: any) {
  return handlingUnit !== "container" && handlingUnitSettings?.l_w_h !== "off";
}

function shouldShowWeightFields(handlingUnitSettings: any) {
  return handlingUnitSettings?.weight !== "off";
}

function normalizeMassUnit(unit: string) {
  if (unit === "kg" || unit === "total_kg") return "kg";
  if (unit === "lbs" || unit === "total_lbs") return "lbs";
  return unit;
}

function convertWeightValue(value: number, sourceUnit: string, targetUnit: string) {
  const source = normalizeMassUnit(sourceUnit);
  const target = normalizeMassUnit(targetUnit);
  if (!source || !target || source === target) {
    return roundDimensionValue(value);
  }
  if (source === "kg" && target === "lbs") {
    return roundDimensionValue(value * 2.20462);
  }
  if (source === "lbs" && target === "kg") {
    return roundDimensionValue(value / 2.20462);
  }
  return roundDimensionValue(value);
}

export function clearDimensionFields(setValue: (name: string, value: unknown) => void) {
  setValue("length", null);
  setValue("width", null);
  setValue("height", null);
}

export function clearWeightField(setValue: (name: string, value: unknown) => void) {
  setValue("weight_piece", null);
}

function applyHandlingUnitDimensionDefaults({
  setValue,
  getRequestFormValues,
  getValues,
  handlingUnit,
  handlingUnitSettings,
}: {
  setValue: (name: string, value: unknown) => void;
  getRequestFormValues: (name: string) => any;
  getValues: (name: string) => any;
  handlingUnit: string | null | undefined;
  handlingUnitSettings: any;
}) {
  if (!handlingUnitSettings || !shouldShowDimensionFields(handlingUnit, handlingUnitSettings)) return;

  const targetMUnit = getValues("m_unit") || getRequestFormValues("packing.global_configuration.m_unit") || "";
  const sourceMUnit = handlingUnitSettings.default_m_unit ?? targetMUnit;

  const hasDefaultLength =
    handlingUnitSettings.default_length !== null && handlingUnitSettings.default_length !== undefined;
  const hasDefaultWidth = handlingUnitSettings.default_width !== null && handlingUnitSettings.default_width !== undefined;
  const hasDefaultHeight =
    handlingUnitSettings.default_height !== null && handlingUnitSettings.default_height !== undefined;

  if (hasDefaultLength) {
    setValue(
      "length",
      convertDimensionValue(Number(handlingUnitSettings.default_length), sourceMUnit, targetMUnit),
    );
  }
  if (hasDefaultWidth) {
    setValue("width", convertDimensionValue(Number(handlingUnitSettings.default_width), sourceMUnit, targetMUnit));
  }
  if (hasDefaultHeight) {
    setValue("height", convertDimensionValue(Number(handlingUnitSettings.default_height), sourceMUnit, targetMUnit));
  }
}

function applyHandlingUnitWeightDefaults({
  setValue,
  getRequestFormValues,
  getValues,
  handlingUnitSettings,
}: {
  setValue: (name: string, value: unknown) => void;
  getRequestFormValues: (name: string) => any;
  getValues: (name: string) => any;
  handlingUnitSettings: any;
}) {
  if (!handlingUnitSettings || !shouldShowWeightFields(handlingUnitSettings)) return;
  if (handlingUnitSettings.default_weight === null || handlingUnitSettings.default_weight === undefined) return;

  const targetWUnit = getValues("w_unit") || getRequestFormValues("packing.global_configuration.w_unit") || "";
  const sourceWUnit = handlingUnitSettings.default_w_unit ?? targetWUnit;

  setValue(
    "weight_piece",
    convertWeightValue(Number(handlingUnitSettings.default_weight), sourceWUnit, targetWUnit),
  );
}

export function applyHandlingUnitFieldDefaults({
  setValue,
  getRequestFormValues,
  getValues,
  handlingUnit,
  handlingUnitSettings,
}: {
  setValue: (name: string, value: unknown) => void;
  getRequestFormValues: (name: string) => any;
  getValues: (name: string) => any;
  handlingUnit: string | null | undefined;
  handlingUnitSettings: any;
}) {
  applyHandlingUnitDimensionDefaults({
    setValue,
    getRequestFormValues,
    getValues,
    handlingUnit,
    handlingUnitSettings,
  });
  applyHandlingUnitWeightDefaults({
    setValue,
    getRequestFormValues,
    getValues,
    handlingUnitSettings,
  });
}
